import { paginatedQuery } from 'lib-shared-api-utils';
import { DeepWritable } from 'ts-essentials';

import { api } from 'app/app.api';

import {
  CreateAwayStatusApiArg,
  CreateAwayStatusApiResponse,
  ReadAwayStatusesApiArg,
  ReadAwayStatusesApiResponse,
  UpdateAwayStatusApiArg,
  UpdateAwayStatusApiResponse,
  UpdateAwayStatusesOrderingApiArg,
} from './types/awayStatusApi.types';
import { PresenceOrganizationWorkloadSettings } from './types/presence.types';
import {
  ReadAgentPresenceApiArg,
  ReadAgentPresenceApiResponse,
  ReadAgentsPresenceV2ApiArg,
  ReadAgentsPresenceV2ApiResponse,
  ReadAgentsPresenceV2TotalApiArg,
  ReadAgentsPresenceV2TotalApiResponse,
  ReadOrganizationWorkloadSettingsApiResponse,
  UpdateOrganizationWorkloadSettingsApiResponse,
  UpdateOtherPresenceMediaApiArg,
  UpdateOtherPresenceMediaApiResponse,
  UpdateOtherPresenceStatusApiArg,
  UpdateOtherPresenceStatusApiResponse,
  UpdatePresenceChannelApiArg,
  UpdatePresenceChannelApiResponse,
  UpdatePresenceMediaApiArg,
  UpdatePresenceMediaApiResponse,
  UpdatePresenceStatusApiArg,
  UpdatePresenceStatusApiResponse,
  UpdateSimultaneousRingApiArg,
  UpdateSimultaneousRingApiResponse,
} from './types/presenceApi.types';
import {
  transformReadAgentPresenceResponse,
  transformReadAgentsPresenceV2Response,
  transformReadAwayStatusesResponse,
} from './utils/transformResponse';

export const presenceApi = api
  .enhanceEndpoints({
    addTagTypes: ['PresenceAwayStatus'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      readAgentPresence: builder.query<
        ReadAgentPresenceApiResponse,
        ReadAgentPresenceApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `presence/${id}`,
        }),
        transformResponse: transformReadAgentPresenceResponse,
      }),
      updateSimultaneousRing: builder.mutation<
        UpdateSimultaneousRingApiResponse,
        UpdateSimultaneousRingApiArg
      >({
        query: ({ id, enabled }) => ({
          version: 1,
          method: 'PUT',
          path: id
            ? `presence/simultaneous_ring/${id}`
            : 'presence/simultaneous_ring',
          body: { enabled },
        }),
      }),
      updatePresenceStatus: builder.mutation<
        UpdatePresenceStatusApiResponse,
        UpdatePresenceStatusApiArg
      >({
        query: ({ status: presence, custom_away_status_id }) => ({
          version: 1,
          method: 'PUT',
          path: 'presence',
          body: { presence, custom_away_status_id },
        }),
      }),
      updateOtherPresenceStatus: builder.mutation<
        UpdateOtherPresenceStatusApiResponse,
        UpdateOtherPresenceStatusApiArg
      >({
        query: ({ agent_id, status: presence, custom_away_status_id }) => ({
          version: 1,
          method: 'PUT',
          path: `presence/${agent_id}`,
          body: { presence, custom_away_status_id },
        }),
      }),
      updatePresenceMedia: builder.mutation<
        UpdatePresenceMediaApiResponse,
        UpdatePresenceMediaApiArg
      >({
        query: (body) => ({
          version: 1,
          method: 'PUT',
          path: 'presence/media',
          body,
        }),
      }),
      updateOtherPresenceMedia: builder.mutation<
        UpdateOtherPresenceMediaApiResponse,
        UpdateOtherPresenceMediaApiArg
      >({
        query: ({ agent_id, media, working }) => ({
          version: 1,
          method: 'PUT',
          path: `presence/media/${agent_id}`,
          body: { media, working },
        }),
      }),
      updatePresenceChannel: builder.mutation<
        UpdatePresenceChannelApiResponse,
        UpdatePresenceChannelApiArg
      >({
        query: (body) => ({
          version: 1,
          method: 'PUT',
          path: 'presence/channel',
          body,
        }),
      }),
      readOrganizationWorkloadSettings: builder.query<
        ReadOrganizationWorkloadSettingsApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          path: 'presence/workload_settings/organization',
        }),
      }),
      updateOrganizationWorkloadSettings: builder.mutation<
        UpdateOrganizationWorkloadSettingsApiResponse,
        PresenceOrganizationWorkloadSettings
      >({
        query: (body) => ({
          version: 1,
          method: 'PUT',
          path: 'presence/workload_settings/organization',
          body,
        }),
        async onQueryStarted(
          {
            load_limits,
            allowed_offers_while_on_call,
            allowed_offers_while_in_chat,
          },
          { dispatch, queryFulfilled },
        ) {
          const patchReadOrganizationWorkloadSettings = dispatch(
            presenceApi.util.updateQueryData(
              'readOrganizationWorkloadSettings',
              undefined,
              (draft) => {
                const mutableData = draft.data as DeepWritable<
                  typeof draft.data
                >;

                mutableData.load_limits = load_limits;
                mutableData.allowed_offers_while_on_call =
                  allowed_offers_while_on_call;
                if (allowed_offers_while_in_chat) {
                  mutableData.allowed_offers_while_in_chat =
                    allowed_offers_while_in_chat;
                }
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch {
            patchReadOrganizationWorkloadSettings.undo();
          }
        },
      }),
      readAgentsPresenceV2: builder.query<
        ReadAgentsPresenceV2ApiResponse,
        ReadAgentsPresenceV2ApiArg
      >({
        query: (arg) =>
          paginatedQuery(
            arg,
            ({
              status,
              hasAssignedConversations: has_assigned_conversations,
              favouriteAgentIds,
            }) => ({
              version: 2,
              path: 'presence',
              params: {
                status,
                has_assigned_conversations,
                ...(favouriteAgentIds && {
                  favourites: favouriteAgentIds.join(','),
                }),
              },
            }),
          ),
        transformResponse: transformReadAgentsPresenceV2Response,
      }),
      readAgentsPresenceV2Total: builder.query<
        ReadAgentsPresenceV2TotalApiResponse,
        ReadAgentsPresenceV2TotalApiArg
      >({
        query: () => ({
          version: 2,
          path: 'presence',
        }),
      }),
      readAwayStatuses: builder.query<
        ReadAwayStatusesApiResponse,
        ReadAwayStatusesApiArg
      >({
        query: (args) => ({
          version: 1,
          path: 'presence/custom-away-status',
          params: { is_deactivated: args?.isDeactivated },
        }),
        providesTags: [{ type: 'PresenceAwayStatus', id: 'LIST' }],
        transformResponse: transformReadAwayStatusesResponse,
      }),
      createAwayStatus: builder.mutation<
        CreateAwayStatusApiResponse,
        CreateAwayStatusApiArg
      >({
        query: (body) => ({
          version: 1,
          path: 'presence/custom-away-status',
          method: 'POST',
          body,
        }),
        invalidatesTags: [{ type: 'PresenceAwayStatus', id: 'LIST' }],
      }),
      updateAwayStatus: builder.mutation<
        UpdateAwayStatusApiResponse,
        UpdateAwayStatusApiArg
      >({
        query: ({ id, ...body }) => ({
          version: 1,
          path: `presence/custom-away-status/${id}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: [{ type: 'PresenceAwayStatus', id: 'LIST' }],
      }),
      updateAwayStatusesOrdering: builder.mutation<
        ReadAwayStatusesApiResponse,
        UpdateAwayStatusesOrderingApiArg
      >({
        query: (body) => ({
          version: 1,
          path: 'presence/custom-away-status/ordering',
          method: 'PATCH',
          body,
        }),
        invalidatesTags: [{ type: 'PresenceAwayStatus', id: 'LIST' }],
      }),
    }),
  });
